import { useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { Container, Grid, MenuItem, useMediaQuery } from "@mui/material";
import InformationBox from "components/InformationBox";
import PageTitle from "components/PageTitle";
import LoadingScreen from "components/Template/LoadingScreen";
import type { Commune, Perimetre, Transfert } from "models";
import { useNavigate } from "react-router";
import { communesService, perimetresService, transfertsService } from "services";
import VisibilityIcon from "@mui/icons-material/Visibility";
import AddHomeIcon from "@mui/icons-material/AddHome";
import EditIcon from "@mui/icons-material/Edit";
import { PerimetreIcon } from "icons";
import PerimetreDeleguesSdeaWithErrorBoundary from "./components/Delegues/PerimetreDeleguesSdea";
import { competencesUtil, perimetresUtil } from "utils";
import { useErrorHandler, withPageErrorBoundary } from "utils/errorHandling";
import PerimetreInformations from "./components/PerimetreInformations";
import PerimetrePorteesInformationsBoxWithErrorBoundary from "./components/Transferts/PerimetrePorteesInformationsBox";
import { ApplicationRole } from "constants/ApplicationRole";
import { routesConfig } from "config/app-config";
import { useRequiredParams } from "hooks";
import TypeTransfert from "constants/TypeTransfert";
import CompetenceChip from "components/Typography/CompetenceChip";
import { ContactsMetiersInformationBox } from "components/Informations/ContactsMetiers/ContactsMetiersInformationBox";
import type { ContactMetierFilters } from "services/contactsMetiersService";
import { conditionsValues } from "constants/ContactsMetiers/conditionsValues";
import PerimetreCommuneList from "./components/PerimetreCommuneList";

function PerimetrePage(): ReactNode {
  const { id } = useRequiredParams<{ id: string }>();
  const navigate = useNavigate();
  const [perimetre, setPerimetre] = useState<Perimetre>();
  const [transferts, setTransferts] = useState<Array<Transfert>>([]);
  const [communes, setCommunes] = useState<Array<Commune>>([]);

  const isPerimetreSansTerritoire =
    perimetre != null ? perimetresUtil.isPerimetreSansTerritoire(perimetre) : false;

  // Il faut mémoiser le filtre et la compétence pour éviter les rendus infinis dans useContactsMetiers
  const memoizedContactsMetiersFilter: Partial<ContactMetierFilters> | undefined = useMemo(
    () =>
      perimetre != null
        ? {
            communesId: communes.map(({ id }) => id),
            perimetresId: [perimetre.id],
            territoiresId: isPerimetreSansTerritoire ? [] : [perimetre.territoire.id],
            competences: [perimetre.competence, conditionsValues.Empty],
          }
        : undefined,
    [perimetre, communes, isPerimetreSansTerritoire]
  );
  const competences = useMemo(() => (perimetre != null ? [perimetre.competence] : []), [perimetre]);

  const { catchErrors } = useErrorHandler();
  const {
    error: transfertsError,
    catchErrors: catchTransfertsError,
    isLoading: isTransfertsLoading,
  } = useErrorHandler(true);
  const { catchErrors: catchCommunesError } = useErrorHandler(true);

  useEffect(() => {
    async function getPerimetre(): Promise<void> {
      const _perimetre = await perimetresService.getById(id);
      document.title = _perimetre.libelle;
      setPerimetre(_perimetre);
    }

    async function getTransferts(): Promise<void> {
      const _transferts = await transfertsService.getByPerimetre(id);
      _transferts.sort(competencesUtil.sortTransfertByCompetence);
      setTransferts(_transferts);
    }

    async function getCommunes(): Promise<void> {
      const _communes = await communesService.getByPerimetre(id);
      setCommunes(_communes);
    }

    setPerimetre(undefined);
    setTransferts([]);
    setCommunes([]);

    void catchErrors(getPerimetre);
    void catchCommunesError(getCommunes);
    void catchTransfertsError(getTransferts);
  }, [catchCommunesError, catchErrors, catchTransfertsError, id]);

  const isSmallScreen = useMediaQuery((theme) => theme.breakpoints.only("xs"));

  if (perimetre == null) {
    return <LoadingScreen />;
  }

  return (
    <Container maxWidth={false}>
      <Grid container spacing={2}>
        <PageTitle
          title={perimetre.libelle}
          icon={<PerimetreIcon />}
          subtitle={
            <>
              Périmètre
              <CompetenceChip competence={perimetre.competence} sx={{ marginX: 1 }} />
              <em>
                {perimetre.typeTransfert === TypeTransfert.PI
                  ? "Transfert complet"
                  : "Transfert partiel"}
              </em>
            </>
          }
        />

        <Grid container spacing={2} size={12}>
          <InformationBox
            hasBackground
            icon={VisibilityIcon}
            articleTitle="Informations générales"
            subtitle={isSmallScreen ? perimetre.code : `Code SDEA : ${perimetre.code}`}
            actionsRoles={ApplicationRole.ADMINISTRATOR}
            actions={
              !isPerimetreSansTerritoire ? (
                <MenuItem
                  key="modifier-perimetre"
                  onClick={() => {
                    navigate(routesConfig.perimetreModify.getParameterPath(perimetre.id));
                  }}>
                  <EditIcon color="primary" sx={{ mr: 1 }} />
                  Modifier les informations du Périmètre
                </MenuItem>
              ) : undefined
            }>
            <PerimetreInformations perimetre={perimetre} />
          </InformationBox>
        </Grid>

        <ContactsMetiersInformationBox
          useContactsMetiersParams={{
            $type: "perimetre",
            collectiviteId: id,
            transferts,
            competences,
            filter: memoizedContactsMetiersFilter,
          }}
        />

        {!isPerimetreSansTerritoire && (
          <Grid container spacing={2} size={12}>
            <InformationBox
              hasBackground
              icon={AddHomeIcon}
              articleTitle="Communes affectées"
              actionsRoles={ApplicationRole.ADMINISTRATOR}
              actions={
                <MenuItem
                  key="modifier-cads"
                  onClick={() => {
                    navigate(
                      routesConfig.perimetreCommunesAssocieesDeleguees.getParameterPath(
                        perimetre.id
                      )
                    );
                  }}>
                  <EditIcon color="primary" sx={{ mr: 1 }} />
                  Affecter les communes fusionnées
                </MenuItem>
              }>
              <PerimetreCommuneList idPerimetre={perimetre.id} />
            </InformationBox>
          </Grid>
        )}

        <Grid container size={12}>
          <PerimetrePorteesInformationsBoxWithErrorBoundary
            transferts={transferts}
            perimetre={perimetre}
            isLoading={isTransfertsLoading}
            error={transfertsError}
          />
        </Grid>

        <Grid container spacing={2} size={12}>
          <InformationBox
            hasBackground
            icon={PerimetreIcon}
            articleTitle="Délégués du périmètre SDEA">
            <PerimetreDeleguesSdeaWithErrorBoundary perimetre={perimetre} />
          </InformationBox>
        </Grid>
      </Grid>
    </Container>
  );
}

const PerimetrePageWithErrorBoundary = withPageErrorBoundary(PerimetrePage);
export default PerimetrePageWithErrorBoundary;
